import { ITEM_LOOKS, ITEM_SHAPES } from "@eldrun/content";
import { Euler, Mesh, PerspectiveCamera, Scene, SRGBColorSpace, Vector3, WebGLRenderTarget, type WebGLRenderer } from "three";
import { createAtmosphereUniforms } from "./atmosphere.ts";
import { itemMesh, itemModel } from "./item-models.ts";
import { models, type ModelLibrary } from "./model-library.ts";
import { createFoliageMaterial } from "./shaders/foliage.ts";

const SIZE = 96;

/** Pose das malhas procedurais no ícone (a de antes da F47). */
const PROCEDURAL_POSE = [0.35, -0.6, 0.2] as const;

/**
 * Data URLs já desenhadas, por biblioteca de modelos (a mesma página reusa entre partidas e entre
 * instâncias: o pré-aquecimento do carregamento serve à cena de jogo).
 */
const caches = new WeakMap<ModelLibrary, Map<string, string>>();

/**
 * Ícones dos itens renderizados das próprias malhas, sob demanda e com cache: cada ícone é desenhado
 * uma vez num render target e vira uma data URL para o DOM. Itens com modelo (F47) usam o LOD 0, com a
 * cor do tier e a pose da forma (as compridas na diagonal); os outros, a malha procedural.
 */
export class ItemIcons {
  private readonly renderer: WebGLRenderer;
  private readonly library: ModelLibrary;
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(30, 1, 0.01, 20);
  private readonly target = new WebGLRenderTarget(SIZE, SIZE);
  private readonly mesh: Mesh;
  private readonly cache: Map<string, string>;
  private readonly atmosphere: ReturnType<typeof createAtmosphereUniforms>;
  private readonly pixels = new Uint8Array(SIZE * SIZE * 4);
  private readonly canvas = document.createElement("canvas");
  private readonly center = new Vector3();
  private readonly euler = new Euler();

  constructor(renderer: WebGLRenderer, library: ModelLibrary = models.library) {
    this.renderer = renderer;
    this.library = library;
    let cache = caches.get(library);
    if (!cache) caches.set(library, (cache = new Map()));
    this.cache = cache;
    this.target.texture.colorSpace = SRGBColorSpace;
    const atmosphere = createAtmosphereUniforms();
    atmosphere.uFogDensity.value = 0;
    atmosphere.uLightDir.value.set(-0.4, 0.8, 0.6).normalize();
    this.atmosphere = atmosphere;
    const material = createFoliageMaterial(atmosphere);
    material.uniforms["uWindStrength"]!.value = 0;
    this.mesh = new Mesh(undefined, material);
    this.scene.add(this.mesh);
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
  }

  /**
   * Data URL do ícone (vazia para slot vazio). Um desenho que falhou (sem contexto 2D, ou com o WebGL
   * perdido) volta vazio e não entra no cache: a próxima chamada tenta de novo.
   */
  url(itemId: string): string {
    if (!itemId) return "";
    const cached = this.cache.get(itemId);
    if (cached !== undefined) return cached;
    const url = this.render(itemId);
    if (url) this.cache.set(itemId, url);
    return url;
  }

  /**
   * Desenha já os ícones que faltam (os itens com modelo, durante "Montando o cenário…"); devolve
   * quantos desenhou agora (0 numa segunda partida da mesma página: o cache já os tem).
   */
  prewarm(ids: Iterable<string>): number {
    let drawn = 0;
    for (const id of ids) {
      if (!id || this.cache.has(id)) continue;
      if (this.url(id)) drawn++;
    }
    return drawn;
  }

  private render(itemId: string): string {
    // Contexto perdido: a leitura sairia transparente e ficaria no cache da página.
    if (this.renderer.getContext().isContextLost()) return "";
    const geometry = itemMesh(itemId, 0, this.library);
    const look = ITEM_LOOKS[itemId];
    const pose = look && itemModel(itemId, 0, this.library) ? ITEM_SHAPES[look.shape].icon : PROCEDURAL_POSE;
    if (!geometry.boundingSphere) geometry.computeBoundingSphere();
    const sphere = geometry.boundingSphere!;
    this.mesh.geometry = geometry;
    this.mesh.rotation.copy(this.euler.set(pose[0], pose[1], pose[2]));
    // O centro da esfera gira com a malha (o punho dos modelos fica longe dele).
    this.center.copy(sphere.center).applyEuler(this.euler);
    const distance = (sphere.radius / Math.sin((this.camera.fov * Math.PI) / 360)) * 1.05;
    this.camera.position.set(this.center.x, this.center.y, this.center.z + distance);
    this.camera.lookAt(this.center);

    const previousTarget = this.renderer.getRenderTarget();
    const previousAlpha = this.renderer.getClearAlpha();
    this.renderer.setClearAlpha(0);
    this.renderer.setRenderTarget(this.target);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    this.renderer.readRenderTargetPixels(this.target, 0, 0, SIZE, SIZE, this.pixels);
    this.renderer.setRenderTarget(previousTarget);
    this.renderer.setClearAlpha(previousAlpha);

    // A leitura vem de baixo para cima: inverte as linhas.
    const context = this.canvas.getContext("2d");
    if (!context) return "";
    const image = context.createImageData(SIZE, SIZE);
    for (let y = 0; y < SIZE; y++) {
      image.data.set(this.pixels.subarray((SIZE - 1 - y) * SIZE * 4, (SIZE - y) * SIZE * 4), y * SIZE * 4);
    }
    context.putImageData(image, 0, 0);
    return this.canvas.toDataURL("image/png");
  }

  dispose(): void {
    this.target.dispose();
    (this.mesh.material as { dispose(): void }).dispose();
  }
}
